/**
 * App lifecycle tracking — observable state for resume/pause transitions.
 *
 * Exposes window.__lifecycle so other modules (Sentry beforeSend, Watchdog L,
 * gate breadcrumbs) can read the most recent transition without subscribing.
 *
 * Listeners registered via onResume/onPause receive the full state snapshot.
 */

import logger from '../utils/logger'

const isBrowser = typeof window !== 'undefined'
const isNative = isBrowser && window.Capacitor?.isNativePlatform?.()

const state = {
  lastResumeAt: null,
  lastPauseAt: null,
  pauseStartedAt: null,
  resumeCount: 0,
  pauseCount: 0,
}

const resumeListeners = new Set()
const pauseListeners = new Set()

if (isBrowser) {
  window.__lifecycle = state
}

function snapshot() {
  let queueStats
  try { queueStats = window.__scoreQueue?.stats?.() } catch { queueStats = null }
  let cacheKeyCount = 0
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && (k.startsWith('press_qcache_') || k.startsWith('press_game_cache_'))) cacheKeyCount++
    }
  } catch { /* ignore */ }
  return {
    currentView: localStorage.getItem('press_current_view'),
    activeMatchId: localStorage.getItem('press_active_match'),
    activeGameId: localStorage.getItem('press_active_game'),
    online: navigator.onLine,
    queueStats,
    cacheKeyCount,
  }
}

export function notifyResumed() {
  const now = Date.now()
  const pausedFor = state.pauseStartedAt ? now - state.pauseStartedAt : null
  state.lastResumeAt = now
  state.pauseStartedAt = null
  state.resumeCount += 1
  const snap = snapshot()
  logger.info('App resumed', { pausedForMs: pausedFor, ...snap })
  for (const fn of resumeListeners) {
    try { fn({ pausedForMs: pausedFor, ...snap }) } catch { /* ignore */ }
  }
}

export function notifyPaused() {
  const now = Date.now()
  state.lastPauseAt = now
  state.pauseStartedAt = now
  state.pauseCount += 1
  const snap = snapshot()
  logger.info('App paused', snap)
  for (const fn of pauseListeners) {
    try { fn(snap) } catch { /* ignore */ }
  }
}

export function onResume(fn) {
  resumeListeners.add(fn)
  return () => resumeListeners.delete(fn)
}

export function onPause(fn) {
  pauseListeners.add(fn)
  return () => pauseListeners.delete(fn)
}

export function getLifecycleState() {
  return state
}

export function msSinceResume() {
  if (!state.lastResumeAt) return null
  return Date.now() - state.lastResumeAt
}

let initialized = false

export function initLifecycle() {
  if (initialized || !isBrowser) return
  initialized = true

  if (isNative) {
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) notifyResumed()
        else notifyPaused()
      })
    }).catch(() => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') notifyResumed()
        else notifyPaused()
      })
    })
  } else {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') notifyResumed()
      else notifyPaused()
    })
  }
}
